import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentQuotationTask } from '@/providers/QuotationTaskProvider';
import { useEffect, useState } from 'react';
import { useQuotationTaskComments, useQuotationTaskCommentsInvalidate } from '@/hooks/api/1590_quotation_task_comments';
import { useCreateQuotationTaskComment } from '@/hooks/api/1600_create_quotation_task_comment';
import { Comments as _Comments, OnCreateCommentProps, OnDeleteCommentProps, OnToggleReactionProps, OnUpdateCommentProps } from '@/components/element/Comments';
import { useSalesOrderMembers } from '@/hooks/api/1280_sales_order_members';
import { useParams } from 'react-router-dom';
import { useQuotationTaskCommentReaction } from '@/hooks/api/1601_quotation_task_comment_reactions';
import { MentionData } from '@draft-js-plugins/mention';
import { useSalesOrderTargetUsers } from '@/hooks/api/1231_sales_order_target_users';
import { useUpdateQuotationTaskComment } from '@/hooks/api/1602_update_quotation_task_comment';
import { useDeleteQuotationTaskComment } from '@/hooks/api/1603_delete_quotation_task_comment';

export const Comments = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const [mentions, setMentions] = useState<MentionData[]>([]);
  const { data: comments } = useQuotationTaskComments({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });
  const { mutate } = useCreateQuotationTaskComment();
  const { mutate: updateMutate } = useUpdateQuotationTaskComment();
  const { mutate: deleteMutate } = useDeleteQuotationTaskComment();
  const { mutate: reactionMutate } = useQuotationTaskCommentReaction();
  const { invalidate: invalidateComments } = useQuotationTaskCommentsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });
  const { data: members } = useSalesOrderTargetUsers({ identifier: _w_!.identifier, team_id: _t_!.id, id: Number(sales_order_id) });

  useEffect(() => {
    if (members) {
      setMentions(() =>
        members.map((d) => ({
          id: d.id,
          name: d.user_account.name,
          avatar: d.user_account.icon_file_url,
        }))
      );
    }
  }, [members]);

  const onCreateComment = ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => {
    mutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: Number(sales_order_id),
        task_id: task!.id,
        content: content!,
        content_json: content_json,
        parent_id: parent_id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onUpdateComment = ({ id, content, content_json, setComment, setError }: OnUpdateCommentProps) => {
    updateMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: Number(sales_order_id),
        task_id: task!.id,
        id: id,
        content: content!,
        content_json: content_json,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onDeleteComment = ({ id, setComment, setError }: OnDeleteCommentProps) => {
    deleteMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: Number(sales_order_id),
        task_id: task!.id,
        id: id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
          invalidateComments();
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onToggleReaction = ({ comment_id, emoji_id, content, setComment, setError }: OnToggleReactionProps) => {
    reactionMutate(
      { identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: Number(sales_order_id), task_id: task!.id, id: comment_id, emoji_id, content },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => setError(error),
        onSettled: () => {},
      }
    );
  };

  if (mentions.length == 0) return <></>;

  return (
    <_Comments
      identifier={`quotation_${_w_!.identifier}_${_t_!.id}_${sales_order_id}_${task!.id}`}
      comments={comments}
      user={me!}
      mentions={mentions}
      onCreateComment={onCreateComment}
      onToggleReaction={onToggleReaction}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
};
